<template>
    <pc-capsule
        v-bind:shown="showCapsule"
        v-bind:title="title"
        has-body
        v-on:update:shown="showCapsule = $event">
        <div>
            <pc-table
                id="ConditionsTable"
                v-bind:columns="columns"
                v-bind:items="visibleConditions"
                v-bind:items-per-page="10"
                v-bind:empty-text="conditionsCapsuleEmptyText"
                card-hide-headers
                key-prop="ConditionGroupId"
                hide-search>
                <template v-slot:ConditionDescription="condition">
                    <div>
                        {{ conditionDescription(condition) }}
                    </div>
                </template>
                <template v-slot:ConditionActions="condition">
                    <div v-if="!readonly">
                        <pc-button
                            id="ModifyConditionButton"
                            link
                            v-on:click="modifyCondition(condition)">
                            {{ modifyConditionButtonText }}
                        </pc-button>
                        <pc-button
                            id="RemoveConditionButton"
                            link
                            v-on:click="removeCondition(condition)">
                            {{ removeConditionButtonText }}
                        </pc-button>
                    </div>
                </template>
            </pc-table>
            <pc-button
                v-if="!readonly"
                id="AddConditionButton"
                primary
                v-on:click="addCondition">
                {{ addConditionButtonText }}
            </pc-button>
        </div>
    </pc-capsule>
</template>
<script>
    import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
    import { RELATIONAL_OPERATOR_CONSTANT } from "Types/elementTypes";
    import { NEW_CONDITION_TAG } from "./editIncentiveDesign.vue";
    import editEligibilityConditionDialog from "./editEligibilityConditionDialog.vue";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";
    import { KEYS, NAMESPACE } from "./i18n/conditionsCapsule.i18n";
    import { mapI18n } from "Util/i18n";

    export default {
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            title: { type: String, required: true },
            readonly: { type: Boolean, required: true },
            conditions: { type: Array, required: true },
            operators: { type: Object, required: true },
            dataFields: { type: Array, required: true },
            listCollection: { type: Object, required: true }
        },
        emits: ["addcondition", "modifycondition", "removecondition"],
        data() {
            return {
                showCapsule: true,
                visibleConditions: this.getVisibleConditions()
            };
        },
        computed: {
            columns() {
                return [
                    {
                        header: this.conditionsTableDescriptionColumnHeader,
                        property: "ConditionDescription",
                        orderable: true,
                        searchable: false,
                        visible: true,
                        sortValue(item) {
                            return item.Description;
                        }
                    },
                    {
                        header: "ConditionGroupId",
                        property: "ConditionGroupId",
                        orderable: false,
                        searchable: false,
                        visible: false
                    },
                    {
                        header: this.conditionsTableActionsColumnHeader,
                        property: "ConditionActions",
                        orderable: false,
                        searchable: false,
                        visible: true
                    }
                ];
            },
            // Localization
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            )
        },
        methods: {
            editEligibilityConditionDialogTitle(isNew) {
                return isNew ? this.addConditionDialogTitle : this.modifyConditionDialogTitle;
            },
            getVisibleConditions() {
                return this.conditions
                    .filter((c) => c.IsDeleted === false)
                    .sort((a, b) => this.conditionDescription(a).localeCompare(this.conditionDescription(b)));
            },
            updateVisibleConditions() {
                this.visibleConditions = [];
                this.visibleConditions = this.getVisibleConditions();
            },
            dataField(dataFieldId) {
                return this.dataFields.find((df) => df.PublicId === dataFieldId);
            },
            operatorText(operatorId) {
                switch (operatorId) {
                    case RELATIONAL_OPERATOR_CONSTANT.IsEqualTo:
                        return "=";
                    case RELATIONAL_OPERATOR_CONSTANT.IsNotEqualTo:
                        return "!=";
                    case RELATIONAL_OPERATOR_CONSTANT.IsGreaterThan:
                        return ">";
                    case RELATIONAL_OPERATOR_CONSTANT.IsLessThan:
                        return "<";
                    case RELATIONAL_OPERATOR_CONSTANT.IsEmpty:
                        return "Is Empty";
                    case RELATIONAL_OPERATOR_CONSTANT.IsNotEmpty:
                        return "Is Not Empty";
                    case RELATIONAL_OPERATOR_CONSTANT.IsGreaterThanOrEqual:
                        return ">=";
                    case RELATIONAL_OPERATOR_CONSTANT.IsLessThanOrEqual:
                        return "<=";
                    default:
                        return "";
                }
            },
            operandText(dataFieldType, operatorId, operandValue) {
                if (
                    operandValue === "" ||
                    operatorId === RELATIONAL_OPERATOR_CONSTANT.IsEmpty ||
                    operatorId === RELATIONAL_OPERATOR_CONSTANT.IsNotEmpty
                ) {
                    return "";
                } else {
                    if (dataFieldType === DATA_FIELD_TYPE.Choice) {
                        const operandValueArray = operandValue.split(";");
                        const operandTextArray = operandValueArray.map(
                            (v) => this.listCollection.ListItems.find((i) => i.ListItemId.toString() === v).Name
                        );
                        const joiner =
                            operatorId === RELATIONAL_OPERATOR_CONSTANT.IsEqualTo
                                ? this.multipleChoiceOrJoiner
                                : this.multipleChoiceAndJoiner;
                        return operandTextArray.join(joiner);
                    } else if (dataFieldType === DATA_FIELD_TYPE.Boolean) {
                        return operandValue.toLowerCase() === "false" || operandValue === "0"
                            ? this.falseValue
                            : this.trueValue;
                    } else {
                        return operandValue;
                    }
                }
            },
            conditionDescription(condition) {
                let descr = "";
                if (condition.DataFieldDefinitionId) {
                    const dataField = this.dataField(condition.DataFieldDefinitionId);
                    const dataFieldName = dataField.DisplayName;
                    if (condition.OperatorId) {
                        const operatorText = this.operatorText(condition.OperatorId);
                        descr = dataFieldName + " " + operatorText;
                        const operandText = this.operandText(
                            dataField.DataFieldTypeId,
                            condition.OperatorId,
                            condition.RightOperand
                        );
                        if (operandText) {
                            descr += " " + operandText;
                        }
                    }
                }
                return descr;
            },
            async addCondition() {
                const condition = {
                    ConditionGroupId: this.$idGenerator.getNextId(NEW_CONDITION_TAG)
                };
                if (await this.editCondition(condition, true)) {
                    this.$emit("addcondition", condition);
                    this.updateVisibleConditions();
                }
            },
            async modifyCondition(condition) {
                if (await this.editCondition(condition, false)) {
                    this.$emit("modifycondition", condition);
                    this.updateVisibleConditions();
                }
            },
            async editCondition(condition, isNew) {
                const title = this.editEligibilityConditionDialogTitle(isNew);
                const dialogResult = await this.$app.showDialog({
                    title: title,
                    customComponent: editEligibilityConditionDialog,
                    customComponentOptions: {
                        condition: condition,
                        dataFields: this.dataFields,
                        operators: this.operators,
                        listCollection: this.listCollection,
                        isNew: isNew
                    },
                    customComponentResultHook: "resultHook",
                    customComponentIsValidToCloseHookAsync: "verifyCanClose",
                    buttons: [
                        {
                            text: this.editConditionCancelButtonText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: this.editConditionSaveButtonText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });
                if (dialogResult.action === DIALOG_RESULT.cancel) {
                    return false;
                }
                condition.DataFieldDefinitionId = dialogResult.data.NewDataField;
                condition.OperatorId = dialogResult.data.NewOperator;
                condition.RightOperand = dialogResult.data.NewOperand;
                condition.Description = this.conditionDescription(condition);
                condition.IsDeleted = false;
                return true;
            },
            async removeCondition(condition) {
                const confirm = await this.$app.showConfirmationDialog(
                    condition.Description,
                    this.removeConditionConfirmText
                );
                if (!confirm) {
                    return;
                }
                this.$emit("removecondition", condition.ConditionGroupId);
                this.updateVisibleConditions();
            }
        }
    };
</script>
